import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Input from '../../components/common/Input';
import Button from '../../components/common/Button';
import { useToast } from '../../context/ToastContext';
import { loginCompany } from '../../api/company';
import { useAuth } from '../../context/AuthContext';

const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

const CompanyLogin = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { login } = useAuth();
  
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm({
    resolver: zodResolver(loginSchema)
  });

  const onSubmit = async (data) => {
    try {
      const response = await loginCompany(data.email, data.password);
      const { access_token, company_id, role } = response;
      
      login({
        token: access_token,
        role: role || 'company',
        companyId: company_id,
        email: data.email
      });
      
      addToast('Welcome to Employer Portal!', 'success');
      navigate('/company/dashboard');
    } catch (err) {
      addToast(err.response?.data?.detail || 'Invalid email or password', 'error');
    }
  };

  return (
    <div className="auth-page" style={{ background: 'linear-gradient(135deg, var(--gray-50) 0%, var(--gray-200) 100%)' }}>
      <div className="auth-card">
        <Link to="/" className="auth-logo justify-center">
          <div className="auth-logo-icon bg-gray-900 text-white">M</div>
          <div>Million<span>Minds</span> <span className="text-sm font-normal text-gray-500 ml-2">For Employers</span></div>
        </Link>
        
        <div className="text-center mb-8">
          <h1 className="auth-title">Employer Login</h1>
          <p className="auth-subtitle">Access your recruitment dashboard to manage jobs and candidates.</p>
        </div>
        
        <form onSubmit={handleSubmit(onSubmit)}>
          <Input
            label="Work Email"
            type="email"
            placeholder="hr@company.com"
            {...register('email')}
            error={errors.email?.message}
          />
          
          <div className="relative">
            <Input
              label="Password"
              type="password"
              placeholder="••••••••"
              {...register('password')}
              error={errors.password?.message}
            />
            <Link 
              to="/forgot-password/company" 
              className="absolute right-0 top-0 text-sm font-semi text-primary hover:underline"
            >
              Forgot password?
            </Link>
          </div>
          
          <div className="mt-8">
            <Button type="submit" variant="secondary" className="w-full bg-gray-900 text-white hover:bg-black" size="lg" isLoading={isSubmitting}>
              Sign In to Employer Portal
            </Button>
          </div>
        </form>
        
        <div className="auth-divider">
          <span>OR</span>
        </div>
        
        <div className="flex-col gap-3">
          <Link to="/login" className="btn btn-outline w-full justify-center">
            Sign in as Student
          </Link>
        </div>
        
        <div className="auth-footer">
          Don't have an employer account? <Link to="/company/register">Register your company</Link>
        </div>
      </div>
    </div>
  );
};

export default CompanyLogin;
